/**
 * Which support-ticket card (if any) an assistant message should render as.
 * (Named ticketCardModel, not ticketCard: macOS disks ignore case, and TicketCard.js is the view.)
 *
 * Structured signals decide, as in utils/ticketFlowSignals.js:
 *   - preview: conversation_state === 'ticket_awaiting_confirm' (on history messages it is a
 *     top-level field; live messages carry it in raw_output), and the text has the backend's
 *     "Subject:" / "Details:" lines. Free-form AI previews have no such lines and stay text.
 *   - success: raw_output.ticket_id, which the backend only sets once the ticket exists.
 * Plan: docs/plans/SUPPORT_TICKET_CARD_PLAN.md
 */

const TICKET_INTENT = 'report_issue';
const AWAITING_CONFIRM = 'ticket_awaiting_confirm';

const FIELD = /^(Location|Subject|Details|Message|Issue):\s*(.*)$/;

const parsePreview = (content) => {
  const lines = content.split(/\r?\n/);
  const fields = {};
  const intro = [];
  const prompt = [];
  let current = null; // field still collecting continuation lines
  let seenField = false;

  lines.forEach((line) => {
    const match = line.match(FIELD);
    if (match) {
      const key = match[1] === 'Message' || match[1] === 'Issue' ? 'details' : match[1].toLowerCase();
      fields[key] = match[2].trim();
      current = key;
      seenField = true;
      return;
    }
    if (!line.trim()) {
      current = null;
      return;
    }
    if (current === 'details') fields.details = `${fields.details}\n${line.trim()}`;
    else if (!seenField) intro.push(line.trim());
    else prompt.push(line.trim());
  });

  if (!fields.subject || !fields.details) return null;
  return {
    type: 'preview',
    intro: intro.join(' '),
    location: fields.location || null,
    subject: fields.subject,
    details: fields.details,
    prompt: prompt.join(' '),
  };
};

export const getTicketCard = (message) => {
  if (!message || message.role !== 'assistant' || !message.content) return null;
  const raw = message.raw_output || {};

  const state = message.conversation_state || raw.conversation_state;
  if (state === AWAITING_CONFIRM) return parsePreview(message.content);

  const isTicketTurn = message.intent === TICKET_INTENT || raw.type === TICKET_INTENT;
  if (isTicketTurn && raw.ticket_id !== undefined && raw.ticket_id !== null && raw.ticket_id !== '') {
    const text = message.content
      .split(/\r?\n/)
      .filter((line) => !/^\s*Reference(\s*(number|#))?\s*:/i.test(line))
      .join('\n')
      .trim();
    return { type: 'success', reference: String(raw.ticket_id), message: text };
  }

  return null;
};

const isSubmittedTicket = (message) => getTicketCard(message)?.type === 'success';

/**
 * What a ticket card at `index` should show besides its own message:
 *   ticket - for a success card, the preview it confirms (subject / location / details)
 *   image  - the photo the user attached for this ticket, if any
 * Looks back only as far as the previous submitted ticket, so an older ticket's photo or
 * preview is never reused.
 */
export const findTicketContext = (messages, index) => {
  const own = getTicketCard(messages[index]);
  let ticket = null;
  let image = null;
  for (let i = index - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m || isSubmittedTicket(m)) break;
    if (own?.type === 'success' && !ticket) {
      const card = getTicketCard(m);
      if (card?.type === 'preview') ticket = card;
    }
    if (!image && m.role === 'user' && Array.isArray(m.attachments)) {
      image = m.attachments.find((a) => a?.type === 'image' && a.url) || null;
    }
  }
  return { ticket, image };
};

export default getTicketCard;
